import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, MapPin, Loader2, Shield, ShieldAlert, ShieldCheck, AlertTriangle,
  Clock, Navigation, Building2, Calendar, CheckCircle, XCircle,
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Polyline, Circle, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { toast } from 'sonner';

// Fix default-icon paths for Leaflet inside CRA bundling.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const venueIcon = L.divIcon({
  className: 'leaflet-venue-icon',
  html: '<div style="background:#0e7490;color:#fff;border-radius:50%;width:28px;height:28px;display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.3);font-size:14px;font-weight:bold;">V</div>',
  iconSize: [28, 28],
  iconAnchor: [14, 14],
});

const checkInIcon = L.divIcon({
  className: 'leaflet-checkin-icon',
  html: '<div style="background:#16a34a;color:#fff;border-radius:50%;width:24px;height:24px;display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.3);font-size:11px;">IN</div>',
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const fmtDt = (s) => (s ? new Date(s).toLocaleString() : '—');
const fmtSecs = (n) => {
  if (!n) return '0s';
  const m = Math.floor(n / 60);
  const s = Math.floor(n % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

/**
 * AdminGPSTrail — Iter-263
 * Admin tool to replay a booking's GPS trail on a map, with attendance
 * verification status surfaced and quick clear/reject controls.
 */
const AdminGPSTrail = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [trail, setTrail] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(false);
  const [reviewNotes, setReviewNotes] = useState('');

  useEffect(() => {
    if (!bookingId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [trailRes, flagsRes] = await Promise.all([
          api.get(`/gps/admin/breadcrumbs/${bookingId}`),
          api.get('/gps/admin/attendance-flags?limit=200'),
        ]);
        if (cancelled) return;
        setTrail(trailRes.data);
        const flagged = (flagsRes.data || []).find((b) => b.booking_id === bookingId);
        setAttendance(flagged || null);
      } catch (e) {
        if (!cancelled) toast.error('Failed to load GPS trail');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [bookingId]);

  const center = useMemo(() => {
    if (trail?.venue_location) return [trail.venue_location.lat, trail.venue_location.lng];
    if (trail?.clock_in_location) return [trail.clock_in_location.lat, trail.clock_in_location.lng];
    if (trail?.breadcrumbs?.length) return [trail.breadcrumbs[0].latitude, trail.breadcrumbs[0].longitude];
    return [39.8283, -98.5795]; // continental US fallback
  }, [trail]);

  const path = useMemo(() => (
    (trail?.breadcrumbs || []).map((b) => [b.latitude, b.longitude])
  ), [trail]);

  const handleReview = async (decision) => {
    setReviewing(true);
    try {
      await api.post(`/gps/admin/attendance-review/${bookingId}`, {
        decision,
        notes: reviewNotes || null,
      });
      toast.success(`Attendance ${decision === 'clear' ? 'cleared' : 'rejected'}`);
      // refresh
      const flagsRes = await api.get('/gps/admin/attendance-flags?limit=200');
      const flagged = (flagsRes.data || []).find((b) => b.booking_id === bookingId);
      setAttendance(flagged || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Review failed');
    } finally {
      setReviewing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
      </div>
    );
  }

  if (!trail) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center text-slate-500">
        Trail not found.
      </div>
    );
  }

  const reviewStatus = attendance?.attendance_review_status || 'ok';
  const reviewBadge = {
    ok: { color: 'bg-green-100 text-green-800', label: 'OK', icon: <ShieldCheck className="w-3 h-3 mr-1" /> },
    needs_review: { color: 'bg-amber-100 text-amber-800', label: 'Needs Review', icon: <ShieldAlert className="w-3 h-3 mr-1" /> },
    admin_cleared: { color: 'bg-cyan-100 text-cyan-800', label: 'Admin Cleared', icon: <ShieldCheck className="w-3 h-3 mr-1" /> },
    rejected: { color: 'bg-red-100 text-red-800', label: 'Rejected', icon: <ShieldAlert className="w-3 h-3 mr-1" /> },
  }[reviewStatus] || { color: 'bg-slate-100 text-slate-800', label: reviewStatus, icon: null };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-gps-trail-page">
      <header className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Shield className="w-5 h-5 text-cyan-600" />
              GPS Trail · {trail.talent_name}
            </h1>
            <p className="text-xs text-slate-500">{trail.venue_name} · {trail.shift_date}</p>
          </div>
          <Badge className={reviewBadge.color} data-testid="attendance-review-badge">
            {reviewBadge.icon} {reviewBadge.label}
          </Badge>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Map */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <MapPin className="w-4 h-4 text-cyan-600" /> Breadcrumb Replay
                <Badge variant="outline" className="ml-auto">{trail.total_points} points</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[480px] w-full rounded-lg overflow-hidden border" data-testid="leaflet-map">
                <MapContainer center={center} zoom={15} style={{ height: '100%', width: '100%' }}>
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />

                  {trail.venue_location && (
                    <>
                      <Marker position={[trail.venue_location.lat, trail.venue_location.lng]} icon={venueIcon}>
                        <Popup>
                          <strong>{trail.venue_name}</strong>
                          <br /> {trail.venue_address}
                        </Popup>
                      </Marker>
                      <Circle
                        center={[trail.venue_location.lat, trail.venue_location.lng]}
                        radius={150}
                        pathOptions={{ color: '#0e7490', fillColor: '#0e7490', fillOpacity: 0.05 }}
                      />
                    </>
                  )}

                  {trail.clock_in_location && (
                    <Marker position={[trail.clock_in_location.lat, trail.clock_in_location.lng]} icon={checkInIcon}>
                      <Popup>Clock-in location<br />{fmtDt(trail.clock_in_time)}</Popup>
                    </Marker>
                  )}

                  {path.length > 1 && (
                    <Polyline positions={path} pathOptions={{ color: '#0891b2', weight: 3, opacity: 0.7 }} />
                  )}
                </MapContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Shift Summary</CardTitle></CardHeader>
            <CardContent className="text-sm space-y-2 text-slate-700">
              <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-slate-400" /> {trail.company_name}</div>
              <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-slate-400" /> {trail.shift_date}</div>
              <div className="flex items-center gap-2"><Navigation className="w-4 h-4 text-slate-400" /> {trail.venue_city_state}</div>
              <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-slate-400" /> Clock-in: {fmtDt(trail.clock_in_time)}</div>
              <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-slate-400" /> Clock-out: {fmtDt(trail.clock_out_time)}</div>
              <div className="flex items-center gap-2">
                {trail.at_venue
                  ? <span className="text-green-700 flex items-center gap-1"><CheckCircle className="w-4 h-4" /> Clock-in inside venue ({trail.distance_from_venue_meters} m)</span>
                  : trail.distance_from_venue_meters !== null
                    ? <span className="text-amber-700 flex items-center gap-1"><AlertTriangle className="w-4 h-4" /> Clock-in {trail.distance_from_venue_meters} m from venue</span>
                    : <span className="text-slate-500">Venue distance unavailable</span>}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Shield className="w-4 h-4 text-cyan-600" /> Attendance Verification
              </CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2 text-slate-700">
              {attendance ? (
                <>
                  {attendance.gps_disabled_during_shift && (
                    <div className="flex items-start gap-2 text-red-700">
                      <ShieldAlert className="w-4 h-4 mt-0.5" />
                      <div>
                        GPS disabled mid-shift
                        <div className="text-xs text-slate-500">at {fmtDt(attendance.gps_disabled_at)} · {attendance.gps_disabled_reason}</div>
                      </div>
                    </div>
                  )}
                  {attendance.geofence_exit_flagged && (
                    <div className="flex items-start gap-2 text-amber-700">
                      <AlertTriangle className="w-4 h-4 mt-0.5" />
                      <div>
                        Outside event footprint &gt; 15 min
                        <div className="text-xs text-slate-500">Total: {fmtSecs(attendance.time_outside_geofence_seconds)} · first exit {fmtDt(attendance.geofence_first_exit_at)}</div>
                      </div>
                    </div>
                  )}
                  {attendance.attendance_review_notes && (
                    <div className="text-xs text-slate-500">Notes: {attendance.attendance_review_notes}</div>
                  )}
                </>
              ) : (
                <div className="text-green-700 flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> No attendance issues detected.</div>
              )}
            </CardContent>
          </Card>

          {reviewStatus === 'needs_review' && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">Admin Review</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                <textarea
                  className="w-full border border-slate-300 rounded p-2 text-sm"
                  rows={3}
                  placeholder="Optional notes for the audit log…"
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  data-testid="review-notes-input"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleReview('clear')}
                    disabled={reviewing}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                    data-testid="clear-attendance-btn"
                  >
                    {reviewing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                    Clear (Allow Payment)
                  </Button>
                  <Button
                    onClick={() => handleReview('reject')}
                    disabled={reviewing}
                    variant="outline"
                    className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
                    data-testid="reject-attendance-btn"
                  >
                    {reviewing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <XCircle className="w-4 h-4 mr-2" />}
                    Reject
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminGPSTrail;
